/**
 * CapUsageIndicator — Phase 7 / Slot Model (07-04-PLAN).
 *
 * Inline copy element that surfaces in the creator flows above the
 * publish button:
 *
 *   "2 of 3 puzzles" (Free — near limit shows warning style)
 *   "28 of 30 puzzles" (Pro)
 *
 * Behavior:
 *   - Pro users (entitlements.pro=true) → renders null (PAY-05 — Pro users
 *     have 30 slots; the indicator is most meaningful for Free users).
 *   - Free users → "X of 3 puzzles" (no time-based reset copy).
 *   - Warning threshold: `used >= limit - 1` (one slot remaining).
 *   - Warning: color var(--color-wrong), fontWeight 700.
 *   - Normal: color var(--color-textMuted), fontWeight 500.
 *   - aria-live="polite" so count changes are announced without interrupting.
 *
 * Props:
 *   - profile (object) : users/{uid} snapshot
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { useCapState } from "../services/capService";

export default function CapUsageIndicator({ profile }) {
  const { t } = useTranslation();
  const state = useCapState(profile);

  // Pro early-return (PAY-05).
  if (
    profile &&
    profile.entitlements &&
    profile.entitlements.pro === true
  ) {
    return null;
  }

  const used = Number.isFinite(state.used) ? state.used : 0;
  const limit = Number.isFinite(state.limit) ? state.limit : 3; // 3 = free default

  // Guard against a degenerate limit (0 or 1, e.g. a corrupted negative
  // purchasedExtraSlots per IN-02) so the warning threshold and pluralization
  // stay sane. For normal limits (3 free / 30 pro) safeLimit === limit.
  const safeLimit = Math.max(1, limit);

  // Warning threshold: one slot remaining triggers warning style.
  const isWarning = used >= safeLimit - 1;
  const style = {
    display: "inline-block",
    fontFamily: "var(--font-body)",
    fontSize: 13,
    fontWeight: isWarning ? 700 : 500,
    color: isWarning
      ? "var(--color-wrong)"
      : "var(--color-textMuted)",
    padding: "4px 0",
    lineHeight: 1.4,
  };

  return (
    <span style={style} data-testid="cap-usage-indicator" aria-live="polite">
      {t("ui.capUsage.count", { count: safeLimit, used })}
    </span>
  );
}
